import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import { showToast } from "../utils/toast";
import { Icons } from "../utils/icons";
import type { TimeSlot } from "../types";
import { esc } from "../utils/html";

/**
 * Stundenraster der Schule: die festen Unterrichtszeiten, aus denen der Stundenplan
 * später seine Vorgaben zieht.
 *
 * Das Backend speichert das Raster immer als vollständige Liste, weil die Position beim
 * Einfügen und Verschieben ohnehin neu vergeben werden muss. Nach außen verhält sich die
 * Seite trotzdem wie die übrigen Stammdaten: Zeilen bearbeitet man im Modal, jede
 * Aktion wird sofort gespeichert.
 */

// id fuehrt der Server mit: eine vorhandene Zeile behaelt sie, damit die Termine ihre
// Blockzuordnung nicht verlieren. Neue Zeilen haben keine.
interface SlotInput { id?: number; label: string; startTime: string; endTime: string; ueCount: number; }

let slots: TimeSlot[] = [];
let currentEl: HTMLElement | null = null;
/** Länge einer Unterrichtseinheit der Schule in Minuten. */
let ueMinutes = 45;

/** Warum 45 Minuten: steht unter der Einstellung und in ihrem Fenster. */
const UE_HINT = "45 Minuten sind die übliche Unterrichtsstunde. Die Ausbildungs- und Prüfungsverordnungen geben ihre Stunden so an, etwa 2.900 Stunden in der Physiotherapie, und Pflicht und Vorlagen im Lehrplan rechnen damit. Ändere den Wert nur, wenn eure Landesbehörde eine andere Länge vorgibt.";

function toInput(s: TimeSlot): SlotInput {
  return { id: s.id, label: s.label, startTime: s.startTime, endTime: s.endTime, ueCount: s.ueCount ?? 1 };
}

/**
 * Vorschlag für die Unterrichtseinheiten eines Blocks: Länge durch die UE-Länge, anteilig.
 * Bei 45 Minuten zählt ein Block von 90 Minuten als 2 UE und einer von 75 Minuten als
 * 1,67 UE. Gespeichert wird mit sechs Stellen, damit sich viele Blöcke genau aufsummieren.
 */
function suggestUe(startTime: string, endTime: string, ue = ueMinutes): number {
  if (!startTime || !endTime) return 1;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  const minutes = (eh * 60 + em) - (sh * 60 + sm);
  return minutes > 0 ? Math.round((minutes / ue) * 1e6) / 1e6 : 1;
}

/** Auf zwei Stellen, mit Komma. */
function formatUe(n: number): string {
  return (Math.round(n * 100) / 100).toLocaleString("de-DE");
}

/** Zahl aus einem Feld, Komma erlaubt; NaN, wenn es keine ist. */
function parseDecimal(raw: string): number {
  const v = raw.trim().replace(",", ".");
  return v === "" ? NaN : Number(v);
}

/** Dauer eines Blocks in Minuten, rein zur Anzeige. */
function durationMinutes(s: SlotInput): number {
  const [sh, sm] = s.startTime.split(":").map(Number);
  const [eh, em] = s.endTime.split(":").map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

/** Vorschlag für den nächsten Block: 45 Minuten im Anschluss an den letzten. */
function suggestNext(): SlotInput {
  const last = slots[slots.length - 1];
  if (!last) return { label: "1. Stunde", startTime: "08:00", endTime: "08:45", ueCount: 1 };
  const [h, m] = last.endTime.split(":").map(Number);
  // Gleiche Länge wie der letzte Block: wer mit 90-Minuten-Blöcken plant, bekommt
  // wieder einen 90-Minuten-Block vorgeschlagen.
  const length = durationMinutes(last) > 0 ? durationMinutes(last) : 45;
  const end = h * 60 + m + length;
  const pad = (n: number) => String(n).padStart(2, "0");
  const endTime = `${pad(Math.floor(end / 60) % 24)}:${pad(end % 60)}`;
  return {
    label: `${slots.length + 1}. Stunde`,
    startTime: last.endTime,
    endTime,
    ueCount: last.ueCount ?? suggestUe(last.endTime, endTime),
  };
}

/** Speichert die übergebene Reihenfolge und zeichnet die Tabelle neu. */
async function persist(next: SlotInput[]): Promise<void> {
  slots = await api.stundenplan.raster.save(next);
  renderTable();
}

function renderTable() {
  const tbody = currentEl?.querySelector("tbody");
  if (!tbody) return;

  const cols = canWrite() ? 7 : 6;
  if (slots.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${cols}" class="empty-state">Noch keine Unterrichtszeiten angelegt</td></tr>`;
    return;
  }

  tbody.innerHTML = slots.map((s, i) => `
    <tr>
      <td class="text-muted">${s.position}</td>
      <td style="font-weight:500">${esc(s.label)}</td>
      <td>${esc(s.startTime)}</td>
      <td>${esc(s.endTime)}</td>
      <td class="text-muted">${durationMinutes(s)} Min.</td>
      <td style="white-space:nowrap">${formatUe(s.ueCount ?? 1)} UE</td>
      ${writeOnly(`<td style="text-align:right;white-space:nowrap">
        <button class="btn btn-sm btn-secondary" data-move="${i}" data-dir="-1" title="Nach oben"${i === 0 ? " disabled" : ""}>↑</button>
        <button class="btn btn-sm btn-secondary" data-move="${i}" data-dir="1" title="Nach unten"${i === slots.length - 1 ? " disabled" : ""}>↓</button>
        <button class="btn btn-sm btn-secondary" data-edit="${i}" style="margin-left:.25rem">Bearbeiten</button>
        <button class="btn btn-sm btn-danger" data-del="${i}">Löschen</button>
      </td>`)}
    </tr>`).join("");
}

function openForm(index?: number) {
  const isEdit = index !== undefined;
  const value: SlotInput = isEdit ? toInput(slots[index]) : suggestNext();
  const readOnly = !canWrite();
  const disabled = readOnly ? " disabled" : "";

  openModal(isEdit ? "Unterrichtszeit bearbeiten" : "Neue Unterrichtszeit", `
    <div class="form-group">
      <label>Bezeichnung *</label>
      <input id="r-label" value="${esc(value.label)}"${disabled} placeholder="z.B. 1. Stunde" />
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Beginn *</label><input type="time" id="r-start" value="${esc(value.startTime)}"${disabled} /></div>
      <div class="form-group"><label>Ende *</label><input type="time" id="r-end" value="${esc(value.endTime)}"${disabled} /></div>
    </div>
    <div class="form-group">
      <label>Zählt als UE *</label>
      <input type="text" inputmode="decimal" id="r-ue" value="${esc(formatUe(value.ueCount))}"${disabled} style="max-width:8rem" />
      <small class="text-muted">Wie viele Unterrichtseinheiten dieser Block ist. Vorschlag: Länge durch ${esc(formatUe(ueMinutes))} Minuten, anteilig${ueMinutes === 45 ? ", ein Block von 90 Minuten zählt also als 2 UE, einer von 75 Minuten als 1,67 UE" : ""}. Danach rechnen Fortschritt, Lehrplan und Auswertung.</small>
    </div>
    <div class="alert alert-error hidden" id="r-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="r-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="r-save">Speichern</button>`)}
    </div>`, { wide: false });

  const mb = modalBody();
  mb.querySelector("#r-cancel")!.addEventListener("click", closeModal);
  if (readOnly) { lockInputs(mb); return; }

  const showError = (message: string) => {
    const box = mb.querySelector("#r-error") as HTMLElement;
    box.textContent = message;
    box.classList.remove("hidden");
  };

  // Die UE folgen den Zeiten, bis jemand sie selbst anfasst. Das Feld zeigt zwei Stellen;
  // solange es unverändert ist, wird der genaue Wert gespeichert (75 Minuten = 1,666667).
  const ueInput = mb.querySelector("#r-ue") as HTMLInputElement;
  let exactUe = value.ueCount;
  let ueTouched = isEdit && Math.abs(value.ueCount - suggestUe(value.startTime, value.endTime)) > 1e-6;
  ueInput.addEventListener("input", () => { ueTouched = true; });
  for (const id of ["#r-start", "#r-end"]) {
    mb.querySelector(id)!.addEventListener("change", () => {
      if (ueTouched) return;
      exactUe = suggestUe(
        (mb.querySelector("#r-start") as HTMLInputElement).value,
        (mb.querySelector("#r-end") as HTMLInputElement).value);
      ueInput.value = formatUe(exactUe);
    });
  }

  mb.querySelector("#r-save")!.addEventListener("click", async () => {
    const entry: SlotInput = {
      label: (mb.querySelector("#r-label") as HTMLInputElement).value.trim(),
      startTime: (mb.querySelector("#r-start") as HTMLInputElement).value,
      endTime: (mb.querySelector("#r-end") as HTMLInputElement).value,
      ueCount: ueInput.value.trim() === formatUe(exactUe) ? exactUe : parseDecimal(ueInput.value),
    };
    if (!Number.isFinite(entry.ueCount) || entry.ueCount <= 0 || entry.ueCount > 20) {
      showError("Die UE müssen eine Zahl über 0 und höchstens 20 sein");
      return;
    }
    if (!entry.label || !entry.startTime || !entry.endTime) {
      showError("Bezeichnung, Beginn und Ende sind Pflichtfelder");
      return;
    }
    if (entry.endTime <= entry.startTime) {
      showError("Das Ende muss nach dem Beginn liegen");
      return;
    }

    const next = slots.map(toInput);
    if (isEdit) next[index] = { ...entry, id: slots[index].id };
    else next.push(entry);

    const saveBtn = mb.querySelector("#r-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      await persist(next);
      closeModal();
      showToast(isEdit ? "Unterrichtszeit gespeichert" : "Unterrichtszeit angelegt");
    } catch (err: any) {
      showError(err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });

  (mb.querySelector("#r-label") as HTMLInputElement).focus();
}

/** Zeile über der Tabelle: UE-Länge der Schule mit Begründung, ohne Kachel. */
function renderUeLine() {
  const box = currentEl?.querySelector<HTMLElement>("#r-ue-line");
  if (!box) return;
  box.innerHTML = `
    <div class="rs-ue-main">Eine UE dauert <strong>${esc(formatUe(ueMinutes))} Minuten</strong>
      ${writeOnly(`<button type="button" class="btn-icon lp-figure-edit" id="r-ue-edit" title="Länge einer UE ändern" aria-label="Länge einer UE ändern">${Icons.pencilSquare}</button>`)}
    </div>
    <p class="rs-ue-hint">${esc(UE_HINT)}${ueMinutes === 45 ? "" : ` Eure Schule rechnet mit ${esc(formatUe(ueMinutes))} Minuten.`}</p>`;
}

/**
 * UE-Länge ändern. Die Vorschau zeigt je Block, wie viele UE er danach zählt, wenn alle
 * Blöcke neu berechnet werden; ohne Haken bleiben ihre Werte und nur neue Vorschläge ändern sich.
 */
function openUeLengthForm() {
  openModal("Länge einer UE", `
    <p class="lp-modal-lead">${esc(UE_HINT)}</p>
    <div class="form-group" style="max-width:12rem">
      <label for="u-min">Eine UE dauert</label>
      <div class="lp-unit"><input type="text" inputmode="decimal" id="u-min" value="${esc(formatUe(ueMinutes))}" /><span>Min.</span></div>
    </div>
    ${slots.length ? `<label class="checkbox-label" style="margin:.5rem 0">
      <input type="checkbox" id="u-recalc" checked />
      <span><span style="font-weight:500">UE aller Blöcke neu berechnen</span>
        <span style="display:block;font-size:.8125rem;color:var(--gray-500)">Auch von Hand eingetragene Werte. Ohne Haken bleiben die Blöcke, wie sie sind.</span></span>
    </label>
    <div class="rs-preview" id="u-preview"></div>` : ""}
    <div class="alert alert-error hidden" id="u-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="u-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="u-save">Speichern</button>
    </div>`, { wide: true, stickyActions: true });

  const mb = modalBody();
  const input = mb.querySelector("#u-min") as HTMLInputElement;
  const recalc = mb.querySelector("#u-recalc") as HTMLInputElement | null;
  const preview = mb.querySelector("#u-preview") as HTMLElement | null;
  const update = () => {
    if (!preview) return;
    const m = parseDecimal(input.value);
    const valid = Number.isFinite(m) && m >= 10 && m <= 240;
    preview.classList.toggle("hidden", !recalc!.checked);
    preview.innerHTML = !valid ? "" : `<table class="rs-preview-table"><tbody>${slots.map(s => {
      const next = suggestUe(s.startTime, s.endTime, m);
      const same = Math.abs(next - s.ueCount) < 1e-6;
      return `<tr${same ? "" : ` class="rs-changed"`}>
        <td>${esc(s.label)}</td><td class="text-muted">${esc(s.startTime)} bis ${esc(s.endTime)}</td>
        <td class="lp-num">${same ? `${esc(formatUe(next))} UE` : `${esc(formatUe(s.ueCount))} → <strong>${esc(formatUe(next))} UE</strong>`}</td>
      </tr>`;
    }).join("")}</tbody></table>`;
  };
  input.addEventListener("input", update);
  recalc?.addEventListener("change", update);
  update();

  mb.querySelector("#u-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#u-save")!.addEventListener("click", async () => {
    const m = parseDecimal(input.value);
    if (!Number.isFinite(m) || m < 10 || m > 240) {
      const box = mb.querySelector("#u-error") as HTMLElement;
      box.textContent = "Bitte eine Länge zwischen 10 und 240 Minuten eingeben";
      box.classList.remove("hidden");
      return;
    }
    const btn = mb.querySelector("#u-save") as HTMLButtonElement;
    btn.disabled = true;
    try {
      const result = await api.stundenplan.raster.setUeLength(m, recalc?.checked === true);
      ueMinutes = result.minutes;
      slots = result.slots;
      closeModal();
      renderUeLine();
      renderTable();
      showToast("Länge einer UE gespeichert");
    } catch (err: any) {
      const box = mb.querySelector("#u-error") as HTMLElement;
      box.textContent = err.message;
      box.classList.remove("hidden");
      btn.disabled = false;
    }
  });
  input.focus();
}

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `
    <div class="settings-page-header">
      <div>
        <h1>Stundenraster</h1>
        <p>Die Zeiten, in denen an dieser Schule unterrichtet wird. Der Stundenplan übernimmt sie als Vorgabe, wenn eine Unterrichtsstunde angelegt wird. Je Block steht, wie viele Unterrichtseinheiten (UE) er zählt.</p>
      </div>
      ${writeOnly(`<button class="btn btn-primary" id="btn-add">+ Neue Unterrichtszeit</button>`)}
    </div>
    ${readOnlyNotice()}
    <div class="rs-ue" id="r-ue-line"></div>
    <div class="card table-wrap">
      <table>
        <thead><tr>
          <th style="width:60px">Nr.</th>
          <th>Bezeichnung</th>
          <th style="width:110px">Beginn</th>
          <th style="width:110px">Ende</th>
          <th style="width:110px">Dauer</th>
          <th style="width:90px">UE</th>
          ${writeOnly(`<th style="width:280px;text-align:right">Aktionen</th>`)}
        </tr></thead>
        <tbody></tbody>
      </table>
    </div>`;

  try {
    [slots, { minutes: ueMinutes }] = await Promise.all([
      api.stundenplan.raster.list(),
      api.stundenplan.raster.ueLength(),
    ]);
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  renderUeLine();
  renderTable();

  el.querySelector("#btn-add")?.addEventListener("click", () => openForm());
  el.querySelector("#r-ue-line")!.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("#r-ue-edit")) openUeLengthForm();
  });

  // Event-Delegation, weil die Zeilen nach jeder Änderung neu geschrieben werden.
  el.querySelector("tbody")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    const edit = target.closest("[data-edit]") as HTMLElement | null;
    if (edit) { openForm(+edit.dataset.edit!); return; }

    const move = target.closest("[data-move]") as HTMLElement | null;
    if (move) {
      const from = +move.dataset.move!;
      const to = from + +move.dataset.dir!;
      if (to < 0 || to >= slots.length) return;
      const next = slots.map(toInput);
      [next[from], next[to]] = [next[to], next[from]];
      try { await persist(next); }
      catch (err: any) { showToast("Fehler: " + err.message); }
      return;
    }

    const del = target.closest("[data-del]") as HTMLElement | null;
    if (del) {
      const index = +del.dataset.del!;
      const slot = slots[index];
      const ok = await confirmDialog({
        title: "Unterrichtszeit löschen",
        message: `„${slot.label}" (${slot.startTime} bis ${slot.endTime}) wird aus dem Stundenraster entfernt.`,
        confirmLabel: "Löschen", danger: true,
      });
      if (!ok) return;
      try {
        await persist(slots.filter((_, i) => i !== index).map(toInput));
        showToast("Unterrichtszeit gelöscht");
      } catch (err: any) { showToast("Fehler: " + err.message); }
    }
  });
}
